/**
 * What the Compute area says, in a person's words: kinds, identities and the little helpers its places share (D447).
 * Nothing here names a route or a package.
 */

import type { ApprovedCluster, ComputeClusterRequest } from '@aioe/shared';

import { VIEW_TONES } from '../ui/Art.tsx';

/** The Compute area's tone, for every place in it. */
export const TONE = VIEW_TONES.compute!;

export const KIND_WORDS: Record<ApprovedCluster['kind'], string> = { kubernetes: 'Kubernetes', aks: 'Azure Kubernetes Service', 'azure-local': 'Azure Local' };

/** "Runs as each person's own sign-in", as a clause after a dot. */
export const runsAs = (identity: ApprovedCluster['identity']) => (identity === 'person' ? 'runs as each person’s own sign-in' : 'runs as the organisation');

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/** Where a cluster is, in a few words: its region, its address or its Azure Local name. */
export function whereWords(c: Pick<ApprovedCluster, 'kind' | 'aks' | 'azureLocal' | 'server'>): string {
  if (c.kind === 'aks') return c.aks ? `region ${c.aks.region}` : 'in Azure';
  if (c.kind === 'azure-local') return c.azureLocal ? `cluster ${c.azureLocal.cluster}` : 'on Azure Local';
  return c.server ? c.server.replace(/^https:\/\//, '') : 'no address';
}

/** The node pools of a cluster, one line each: the name, its tags and the runtime classes it offers. */
export function poolWords(c: Pick<ApprovedCluster, 'nodePools'>): string[] {
  return (c.nodePools ?? []).map((p) => {
    const tags = p.tags.filter((t) => t !== `pool:${p.name}`);
    return `${p.name}${tags.length ? ` (tags ${tags.join(', ')})` : ''}${p.runtimeClasses?.length ? `, runtime ${p.runtimeClasses.join(', ')}` : ''}`;
  });
}

/** What a request is for, in a few words. */
export const requestTarget = (q: ComputeClusterRequest) => `${q.name} (${KIND_WORDS[q.kind]})`;
